import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../../../context/AuthContext";
import { canAccess } from "../../../utils/permissions";

// UI Components
import Header from "../../../components/ui/Header";
import Sidebar from "../../../components/ui/Sidebar";
import Breadcrumb from "../../../components/ui/Breadcrumb";
import Button from "../../../components/ui/Button";
import Icon from "../../../components/AppIcon";
import EditOrderModal from "./modals/EditOrderModal";
// Tabs
import OverviewTab from "./tabs/OverviewTab";
import PatientTab from "./tabs/PatientTab";
import TestsTab from "./tabs/TestsTab";
import VendorTab from "./tabs/VendorTab";
import PaymentTab from "./tabs/PaymentTab";

// Modals
import VendorAssignModal from "./modals/VendorAssignModal";
import AddPaymentModal from "./modals/AddPaymentModal";
import RescheduleOrderModal from "./modals/RescheduleOrderModal";

// Shared
import LoadingSpinner from "./shared/LoadingSpinner";
import ErrorState from "./shared/ErrorState";
import OrderSidebar from "./shared/OrderSidebar";

import toast from "react-hot-toast";
import axiosInstance from "../../../utils/axiosInstance";

const OrderDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const { user: currentUser } = useAuth();

  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [order, setOrder] = useState(null);
  const [vendors, setVendors] = useState([]);
  const [vendorsLoading, setVendorsLoading] = useState(false);
  const [payments, setPayments] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState("tests");

  const [showVendorModal, setShowVendorModal] = useState(false);
  const [showAddPaymentModal, setShowAddPaymentModal] = useState(false);
  const [showRescheduleModal, setShowRescheduleModal] = useState(false);

  // ✅ NEW: result completion summary coming from TestsTab (real source of truth)
  const [resultsSummary, setResultsSummary] = useState({
    allResultsDone: false,
    pendingCount: 0,
    totalCount: 0,
  });

  const fetchAllData = async () => {
    try {
      setIsLoading(true);
      setError(null);

      const [orderRes, paymentsRes] = await Promise.all([
        axios.get(`${import.meta.env.VITE_API_BASE_URL}/api/orders/${id}`),
        axios.get(
          `${import.meta.env.VITE_API_BASE_URL}/api/payments/orders/${id}/payments`,
        ),
      ]);

      setOrder(orderRes.data.order);
      setPayments(paymentsRes.data.payments || []);
    } catch (err) {
      setError(err.response?.data?.message || "Failed to load order details");
    } finally {
      setIsLoading(false);
    }
  };


  const openVendorModal = async () => {
    setShowVendorModal(true);

    // ✅ fetch only first time
    if (vendors.length > 0) return;

    try {
      setVendorsLoading(true);
      const res = await axiosInstance.get(
        `${import.meta.env.VITE_API_BASE_URL}/api/vendors`,
      );
      setVendors(res.data.vendors || []);
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to fetch vendors");
    } finally {
      setVendorsLoading(false);
    }
  };

  useEffect(() => {
    if (id) fetchAllData();
  }, [id]);

  const refreshOrder = () => fetchAllData();

  const formatCurrency = (amount) =>
    new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 0,
    }).format(amount || 0);

  const calculateBalance = () => {
    const total = order?.finalAmount;
    const paid = payments.reduce((sum, p) => sum + (Number(p.amount) || 0), 0);
    return total - paid;
  };

  if (isLoading) return <LoadingSpinner />;

  if (error || !order) {
    return (
      <ErrorState error={error} onBack={() => navigate("/orders-management")} />
    );
  }

  const tabs = [
    { key: "tests", label: "Tests" },
    { key: "overview", label: "Overview" },
    { key: "patient", label: "Patient" },
    { key: "vendor", label: "Vendor" },
    { key: "payment", label: "Payment" },
  ];

  const handleUpdateOrder = async (payload) => {
    try {
      await axios.put(
        `${import.meta.env.VITE_API_BASE_URL}/api/orders/${order.id}/vendor-status`,
        payload,
      );
      toast.success("Order updated successfully");
      fetchAllData();
    } catch (err) {
      toast.error("Failed to update order");
    }
  };



  return (
    <div className="min-h-screen bg-gray-50">
      <Header
        onMenuToggle={() => setSidebarOpen(!sidebarOpen)}
        isMenuOpen={sidebarOpen}
      />
      <Sidebar
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        isCollapsed={sidebarCollapsed}
        onCollapseToggle={() => setSidebarCollapsed(!sidebarCollapsed)}
      />

      <main
        className={`pt-14 transition-all duration-300 ${
          sidebarCollapsed ? "lg:ml-16" : "lg:ml-64"
        }`}>
        <div className="p-4 md:p-6">
          <Breadcrumb className="mb-4" />

          {/* Page Header */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
            <div className="flex items-center gap-3">
              {/* Back button as shopping bag icon */}
              <button
                onClick={() => navigate("/orders-management")}
                className="relative h-10 w-10 bg-white border border-gray-200 rounded-lg flex items-center justify-center shadow-sm hover:bg-gray-50 transition-colors flex-shrink-0">
                <Icon name="ArrowLeft" size={20} className="text-gray-700" />
                {order.isUrgent && (
                  <span className="absolute -top-1.5 -right-1.5 bg-red-500 text-white text-[10px] font-semibold px-2 py-0.5 rounded-full shadow">
                    URGENT
                  </span>
                )}
              </button>
              <div>
                <h1 className="text-lg font-semibold text-gray-900 tracking-tight">
                  Order #{order.id}
                </h1>
                <p className="text-xs text-gray-500 mt-0.5">
                  {new Date(order.createdAt).toLocaleDateString("en-IN", {
                    day: "numeric",
                    month: "short",
                    year: "numeric",
                    hour: "2-digit",
                    minute: "2-digit",
                  })}
                </p>
              </div>
            </div>

            <div className="flex flex-wrap gap-2">
              {order.status !== "cancelled" && canAccess(currentUser, "canEdit") && (
                <Button
                  onClick={() => navigate(`/orders/${id}/edit`)}
                  className="px-3 py-1.5 text-xs h-9 bg-amber-500 hover:bg-amber-600 text-white">
                  <Icon name="ClipboardEdit" size={14} className="mr-1.5" />
                  Edit Order
                </Button>
              )}
            </div>
          </div>

          {/* Summary bar */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 bg-white rounded-lg border border-gray-200 px-4 py-2.5 mb-6">
            <div className="flex flex-wrap items-center gap-2">
              <span
                className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${
                  order.status === "completed"
                    ? "bg-green-100 text-green-800"
                    : order.status === "pending"
                      ? "bg-amber-100 text-amber-800"
                      : order.status === "cancelled"
                        ? "bg-red-100 text-red-800"
                        : "bg-blue-100 text-blue-800"
                }`}>
                <span
                  className={`h-1.5 w-1.5 rounded-full ${
                    order.status === "completed"
                      ? "bg-green-600"
                      : order.status === "pending"
                        ? "bg-amber-600"
                        : order.status === "cancelled"
                          ? "bg-red-600"
                          : "bg-blue-600"
                  }`}
                />
                {order.status?.charAt(0).toUpperCase() + order.status?.slice(1)}
              </span>

              <span className="text-xs text-gray-400">•</span>

              <span className="text-sm font-semibold text-gray-800">
                {formatCurrency(order.finalAmount || 0)}
              </span>

              <span className="text-xs text-gray-400">•</span>

              <span className="text-xs text-gray-600">
                Balance:{" "}
                <span
                  className={`font-semibold ${
                    calculateBalance() > 0 ? "text-red-600" : "text-green-600"
                  }`}>
                  {formatCurrency(calculateBalance())}
                </span>
              </span>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-medium bg-blue-50 text-blue-700 border border-blue-200">
                <span className="opacity-70">Center:</span>
                {order?.center?.name || "-"}
              </span>

              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-medium bg-purple-50 text-purple-700 border border-purple-200">
                <span className="opacity-70">Ref:</span>
                {order?.refCenter?.name || "-"}
              </span>

              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-medium bg-teal-50 text-teal-700 border border-teal-200">
                <span className="opacity-70">Doctor:</span>
                {order?.doctor?.name || "-"}
              </span>
            </div>
          </div>

          {/* Tabs */}
          <div className="border-b border-gray-200 mb-6">
            <nav className="flex flex-wrap gap-4 sm:gap-6 -mb-px">
              {tabs.map((tab) => (
                <button
                  key={tab.key}
                  onClick={() => setActiveTab(tab.key)}
                  className={`relative pb-3 text-xs font-medium transition-colors border-b-2 ${
                    activeTab === tab.key
                      ? "text-blue-600 border-blue-600"
                      : "text-gray-500 hover:text-gray-700 border-transparent"
                  }`}>
                  {tab.label}
                </button>
              ))}
            </nav>
          </div>

          {/* Main Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
            {/* Left */}
            <div className="lg:col-span-2">
              <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
                {activeTab === "overview" && (
                  <OverviewTab order={order} formatCurrency={formatCurrency} />
                )}
                {activeTab === "patient" && <PatientTab order={order} />}
                {activeTab === "tests" && (
                  <TestsTab
                    order={order}
                    orderId={id}
                    formatCurrency={formatCurrency}
                    // ✅ IMPORTANT: THIS ENABLES DISPATCH BUTTON
                    onResultsSummary={setResultsSummary}
                  />
                )}
                {activeTab === "vendor" && (
                  <VendorTab
                    order={order}
                    vendors={vendors}
                    vendorsLoading={vendorsLoading}
                    onOpenModal={openVendorModal}
                  />
                )}
                {activeTab === "payment" && (
                  <PaymentTab
                    order={order}
                    payments={payments}
                    onOpenModal={() => setShowAddPaymentModal(true)}
                  />
                )}
              </div>
            </div>

            {/* Right */}
            <div className="lg:col-span-1">
              <div className="sticky top-4">
                <OrderSidebar
                  order={order}
                  onUpdateOrder={handleUpdateOrder}
                  balance={calculateBalance()}
                  onAddPayment={() => {
                    setActiveTab("payment");
                    setShowAddPaymentModal(true);
                  }}
                  onReschedule={() => setShowRescheduleModal(true)}
                  // ✅ pass results state
                  allResultsDone={resultsSummary.allResultsDone}
                  pendingCount={resultsSummary.pendingCount}
                  totalCount={resultsSummary.totalCount}
                />
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Modals */}
      <VendorAssignModal
        isOpen={showVendorModal}
        onClose={() => setShowVendorModal(false)}
        orderId={id}
        vendors={vendors}
        vendorsLoading={vendorsLoading}
        onSuccess={refreshOrder}
      />

      <AddPaymentModal
        isOpen={showAddPaymentModal}
        onClose={() => setShowAddPaymentModal(false)}
        order={order}
        balance={calculateBalance()}
        onSuccess={refreshOrder}
      />

      <RescheduleOrderModal
        isOpen={showRescheduleModal}
        onClose={() => setShowRescheduleModal(false)}
        order={order}
        onSuccess={refreshOrder}
      />

      {showEditModal && (
        <EditOrderModal
          order={order}
          onClose={() => setShowEditModal(false)}
          onSaved={() => {
            setShowEditModal(false);
            fetchAllData(); // refreshes the order detail
          }}
        />
      )}
    </div>
  );
};

export default OrderDetails;
